import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { IconButton, Stack, Select, MenuItem } from '@mui/material';
import { logger } from '@/services/loggerService';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import {
  ControlsWrapper,
  Marker,
  ProgressBar,
  ProgressBarContainer,
  SnapshotContainer,
  TimeText,
  Video,
  VideoIconBox,
} from './styles';
import useVideoStore from '@/stores/videoStore';
import VolumeControl from '../VolumeControl/VolumeControl';
import { DocumentChapter } from '@/types/note';
import ProcessingMessage from '@/components/ProcessingMessage/ProcessingMessage';
import { useVideoPlayback, useThumbnailPreview, useVolumeControl, useVideoFullscreen } from './hooks';
import { parseTime } from '@/helpers/formatTime';
import { CommentTimelineMarkers } from '@/features/comments';
import SilentErrorBoundary from '@/components/SilentErrorBoundary';

interface VideoPlayerProps {
  src: string;
  chapters?: DocumentChapter[];
  isMobile?: boolean;
  videoReady?: boolean;
  isTrimEnabled?: boolean;
  isProcessing?: boolean;
  onPlay?: () => void;
  posterUrl?: string;
  videoWidth?: number;
  videoHeight?: number;
  noteId?: string;
  shareToken?: string | null;
}

const VideoPlayer: FC<VideoPlayerProps> = ({
  src,
  chapters,
  isMobile = false,
  videoReady = true,
  isTrimEnabled = false,
  isProcessing = false,
  onPlay,
  posterUrl,
  videoWidth,
  videoHeight,
  noteId,
  shareToken,
}) => {
  // Video loading instrumentation
  const vpT0 = useRef(performance.now());
  const vpRender = useRef(0);
  vpRender.current += 1;
  const vpMs = () => (performance.now() - vpT0.current).toFixed(0);

  // UI-specific state
  const [showPlayAndPauseIcon, setShowPlayAndPauseIcon] = useState(false);
  const [temporaryIconShow, setTemporaryIconShow] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const temporaryIconTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Video store for time display
  const { currentTime, remainingTime } = useVideoStore();

  // Holder callback wired after useThumbnailPreview returns the ref. Lets
  // useVideoPlayback's processing-complete reset reload both the main and
  // the hover-preview <video> elements in the same guarded transition,
  // without string-manipulating the signed CDN URL.
  const reloadThumbnailRef = useRef<() => void>(() => {});
  const onProcessingReset = useCallback(() => reloadThumbnailRef.current(), []);

  // Custom hooks for video functionality
  const {
    videoRef,
    progressRef,
    isPlaying,
    isMetadataLoaded,
    hasStartedPlaying,
    duration,
    currentWidth,
    playbackRate,
    setPlaybackRate,
    handleLoadedMetadata,
    playOrPause,
    changeRange,
    handleChapterClick,
  } = useVideoPlayback({ onPlay, isProcessing, onProcessingReset });

  const { thumbnailVideoRef, snapshotRef, snapshots, showThumbnail, onSliderHover, onOutsideSlider } =
    useThumbnailPreview({ videoRef, progressRef });

  reloadThumbnailRef.current = () => thumbnailVideoRef.current?.load();

  const { volume, isMuted, handleVolumeUpdate, handleMuteToggle } = useVolumeControl({ videoRef });

  const { handleFullscreen } = useVideoFullscreen({ videoRef });

  // Fallback: capture actual video dimensions when Firebase metadata is missing
  const [elementAspectRatio, setElementAspectRatio] = useState<string | null>(null);

  // Use Firebase metadata first, then video element dimensions, then 16:9 fallback
  const spacerAspectRatio =
    videoWidth && videoHeight ? `${videoWidth} / ${videoHeight}` : elementAspectRatio || '16 / 9';

  // Wrap metadata handler to capture video element dimensions when Firebase metadata is missing
  const onVideoMetadataLoaded = useCallback(() => {
    handleLoadedMetadata();
    if (!videoWidth || !videoHeight) {
      const v = videoRef.current;
      if (v && v.videoWidth > 0 && v.videoHeight > 0) {
        setElementAspectRatio(`${v.videoWidth} / ${v.videoHeight}`);
      }
    }
  }, [handleLoadedMetadata, videoWidth, videoHeight]);

  // Log every render with key state
  logger.debug('video', `VideoPlayer render #${vpRender.current} [+${vpMs()}ms]`, {
    isMetadataLoaded,
    isProcessing,
    hasStartedPlaying,
    showControls,
    hasPosterUrl: Boolean(posterUrl),
    srcLength: src?.length ?? 0,
    videoWidth,
    videoHeight,
  });

  // Play/pause with temporary icon display
  const playOrPauseAndShowIcon = () => {
    playOrPause();

    // Only show temporary icon when not in processing mode (playbar is visible)
    if (!isProcessing) {
      setTemporaryIconShow(true);
      if (temporaryIconTimeoutRef.current) clearTimeout(temporaryIconTimeoutRef.current);
      temporaryIconTimeoutRef.current = setTimeout(() => setTemporaryIconShow(false), 1500);
    }
  };

  // Clear the temporary icon timer on unmount so it can't fire setState afterwards
  useEffect(() => {
    return () => {
      if (temporaryIconTimeoutRef.current) clearTimeout(temporaryIconTimeoutRef.current);
    };
  }, []);

  // Adjust marker position for chapter markers
  const adjustMarkerPosition = (time: number, videoDuration: number) => {
    const normalized = time / videoDuration;
    const midPoint = 0.5;
    const scale = 0.99;
    return midPoint + (normalized - midPoint) * scale;
  };

  // Delay showing controls so the video has time to render its first frame
  useEffect(() => {
    if (isMetadataLoaded && !isProcessing) {
      const timer = setTimeout(() => setShowControls(true), 200);
      return () => clearTimeout(timer);
    }
    setShowControls(false);
  }, [isMetadataLoaded, isProcessing]);

  // Show play button persistently during processing when video is paused
  useEffect(() => {
    if (isProcessing && !isPlaying) {
      setShowPlayAndPauseIcon(true);
    } else if (!isProcessing || isPlaying) {
      setShowPlayAndPauseIcon(false);
    }
  }, [isProcessing, isPlaying]);

  return (
    // Reserve min width and height to prevent layout shift when loading
    <Stack
      position="relative"
      sx={{
        width: '100%',
        maxWidth: isMobile ? '100%' : '800px',
      }}
    >
      {/* Processing message appears above the video */}
      {!videoReady && (
        <Stack
          sx={{
            mb: 2,
            width: '100%',
          }}
        >
          <ProcessingMessage
            isVisible={isTrimEnabled && !videoReady}
            message="Your video is processing and will be ready soon."
            type="video"
            variant="inline"
          />
        </Stack>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr)',
          width: '100%',
          maxHeight: isMobile ? '60vh' : '70vh',
          borderRadius: isMobile ? '12px' : '22px',
          overflow: 'hidden',
          backgroundColor: 'transparent',
        }}
      >
        {/* Spacer holds height so content below never shifts — uses real dimensions when available */}
        <div style={{ gridRow: 1, gridColumn: 1, aspectRatio: spacerAspectRatio }} />
        <div
          style={{
            gridRow: 1,
            gridColumn: 1,
            position: 'relative',
          }}
        >
          <Video
            src={src}
            ref={videoRef}
            preload="auto"
            crossOrigin="anonymous"
            playsInline
            poster={posterUrl}
            onLoadedMetadata={onVideoMetadataLoaded}
            onClick={playOrPauseAndShowIcon}
            style={{
              maxHeight: isMobile ? '60vh' : '70vh',
              borderRadius: isMobile ? '12px' : '22px',
            }}
          >
            Your browser does not support the video tag.
          </Video>
          {(showPlayAndPauseIcon || temporaryIconShow || (!isPlaying && !hasStartedPlaying)) && (
            <VideoIconBox
              sx={!isPlaying && !hasStartedPlaying && !temporaryIconShow ? { animation: 'none', opacity: 1 } : {}}
            >
              <IconButton
                onClick={playOrPauseAndShowIcon}
                sx={{
                  color: 'white',
                  fontSize: 48,
                }}
                disableRipple
              >
                {isPlaying ? <PauseRoundedIcon fontSize="inherit" /> : <PlayArrowRoundedIcon fontSize="inherit" />}
              </IconButton>
            </VideoIconBox>
          )}
        </div>
      </div>

      {!isProcessing && (
        <ControlsWrapper
          sx={{
            opacity: showControls ? 1 : 0,
            transition: 'opacity 0.8s ease-in',
            pointerEvents: showControls ? 'auto' : 'none',
          }}
        >
          {isPlaying ? (
            <PauseRoundedIcon cursor="pointer" onClick={playOrPause} />
          ) : (
            <PlayArrowRoundedIcon cursor="pointer" onClick={playOrPause} />
          )}
          <TimeText variant="caption" width="42px">
            {currentTime}
          </TimeText>

          <ProgressBarContainer>
            <ProgressBar
              type="range"
              step="any"
              ref={progressRef}
              onChange={changeRange}
              value={videoRef.current?.currentTime || 0}
              min="0"
              currentWidth={currentWidth}
              onMouseMove={onSliderHover}
              onMouseOut={onOutsideSlider}
            />
            {chapters?.map((chapter, index) => {
              const chapterTime = parseTime(chapter.timecode);
              return (
                <Marker
                  key={`${index}-${chapterTime}`}
                  style={{ left: `${adjustMarkerPosition(chapterTime, duration) * 100}%` }}
                  onClick={() => handleChapterClick(chapterTime)}
                  onMouseMove={onSliderHover}
                  onMouseOut={onOutsideSlider}
                />
              );
            })}
            <SilentErrorBoundary feature="video" operation="timelineMarkers">
              <CommentTimelineMarkers noteId={noteId} shareToken={shareToken} />
            </SilentErrorBoundary>
          </ProgressBarContainer>
          <TimeText variant="caption" width="48px">
            {remainingTime}
          </TimeText>
          {!isMobile && (
            <VolumeControl
              volume={volume}
              isMuted={isMuted}
              onVolumeChange={handleVolumeUpdate}
              onMuteToggle={handleMuteToggle}
            />
          )}
          {/* Playback speed selector */}
          <Select
            value={playbackRate}
            onChange={(e) => setPlaybackRate(Number(e.target.value))}
            size="small"
            sx={{
              color: 'white',
              '.MuiSelect-icon': { color: 'white' },
              fontSize: '0.75rem',
              marginLeft: '8px',
              // ensure the dropdown is wide enough for longer labels
              minWidth: isMobile ? 60 : 80,
              display: isMobile ? 'none' : 'flex', // hide on mobile to save space
            }}
          >
            <MenuItem value={0.5}>.5x</MenuItem>
            <MenuItem value={0.75}>.75x</MenuItem>
            <MenuItem value={1}>1x</MenuItem>
            <MenuItem value={1.25}>1.25x</MenuItem>
            <MenuItem value={1.5}>1.5x</MenuItem>
            <MenuItem value={2}>2x</MenuItem>
          </Select>
          {/* Fullscreen button */}
          <FullscreenIcon onClick={handleFullscreen} sx={{ cursor: 'pointer', color: 'white', marginLeft: '8px' }} />
        </ControlsWrapper>
      )}

      <SnapshotContainer ref={snapshotRef} id="snapshot-container" isHovered={showThumbnail}>
        <img src={snapshots}></img>
      </SnapshotContainer>
      <Video
        src={src}
        ref={thumbnailVideoRef}
        preload="auto"
        crossOrigin="anonymous"
        playsInline
        sx={{ display: 'none' }}
      ></Video>
    </Stack>
  );
};

export default VideoPlayer;
